import { successAlert } from '@/components/others/ToastGroup';
import UserContext from '@/context/UserContext';
import { UserInfo } from '@/utils/types';
import { walletConnect } from '@/utils/util';
import usePersistStore from '@/zustand-store/persist-config/usePersistStore';
import { useWallet } from '@solana/wallet-adapter-react';
import { useContext, useEffect } from 'react';
import { useSolanaConnect } from './useConnectSolana';
import { useEvmConnect } from './useConnectEvm';
import { usePersistActions } from '@/zustand-store/persist-config/selector';

export const useWalletManager = () => {
  // common state
  const { user, currentNetwork } = usePersistStore();
  const { setUser, setEvmTypeWallet } = usePersistActions();
  const { login, setLogin, setIsLoading } = useContext(UserContext);

  // solana connect states
  const {
    logOut: handleLogoutSolana,
    confirmWalletWithSigature: confirmWalletWithSigatureSolana,
  } = useSolanaConnect();
  const { publicKey } = useWallet();

  // evm connect states
  const {
    logout: handleLogoutEvm,
    confirmWalletWithSigature: confirmWalletWithSigatureEvm,
  } = useEvmConnect();

  // re-login after detected change address in solana
  useEffect(() => {
    if (currentNetwork === 'evm') return;
    const handleClick = async () => {
      if (publicKey && !login) {
        const updatedUser: UserInfo = {
          name: publicKey.toBase58().slice(0, 6),
          wallet: publicKey.toBase58(),
          isLedger: false,
        };
        await handleLogin(updatedUser);
      }
    };
    handleClick();
  }, [publicKey, login, currentNetwork]);

  // logOut for all networks
  const logOut = async () => {
    await handleLogoutSolana();
    await handleLogoutEvm();
    setUser({} as UserInfo);
    setLogin(false);
    setEvmTypeWallet('');
  };

  const handleLogin = async (updatedUser: UserInfo) => {
    try {
      const connection = await walletConnect({ data: updatedUser });
      if (!connection) return;
      if (connection.nonce === undefined) {
        const newUser = {
          name: connection.name,
          wallet: connection.wallet,
          _id: connection._id,
          avatar: connection.avatar,
        };
        setUser(newUser as UserInfo);
        setLogin(true);
        return;
      }

      const confirm =
        currentNetwork === 'solana'
          ? await confirmWalletWithSigatureSolana(connection)
          : await confirmWalletWithSigatureEvm(connection);
      if (confirm) {
        setUser(confirm);
        setLogin(true);
        setIsLoading(false);
      }
      successAlert('Message signed.');
    } catch (error) {
      console.log('error', error);
      throw new Error(error);
    }
  };

  return {
    user,
    logOut,
    handleLogin,
  };
};
